import React from 'react';
import PropTypes from 'prop-types';

/**
 * A preview of the web chat, drawn with the same shapes the widget itself uses:
 * one ground under the header and the conversation, the identity card floating
 * over it, rounded bubbles, and the input as a single pill.
 *
 * It replaced an exported illustration whose white header bar, square bubbles
 * and underlined input no longer exist in the widget, and whose colour legend
 * relied on eight leader lines happening to line up with the eight inputs beside
 * it - those inputs carry their own labels now.
 *
 * The menu is drawn open although the real one opens on a click: every colour
 * on this card has to be visible while it is being picked, and three of them -
 * the bot's background and text, and the visitor's colour on the icons - are
 * nowhere else in this view.
 */

const styles = {
    frame: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        borderRadius: '14px',
        overflow: 'hidden',
        border: '1px solid rgba(9, 24, 58, 0.08)',
        fontSize: '11px',
        lineHeight: '15px',
    },
    header: {
        position: 'relative',
        flexShrink: 0,
        padding: '8px 10px 10px',
    },
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '4px',
    },
    action: {
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: 'rgba(9, 24, 58, 0.06)',
    },
    actionGroup: {
        display: 'flex',
        gap: '6px',
        marginLeft: 'auto',
    },
    menu: {
        position: 'absolute',
        top: '30px',
        right: '10px',
        zIndex: 1,
        width: '116px',
        padding: '4px',
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(9, 24, 58, 0.16)',
    },
    menuRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 6px',
        fontSize: '10px',
        lineHeight: '14px',
        whiteSpace: 'nowrap',
    },
    menuIcon: {
        display: 'flex',
        flexShrink: 0,
    },
    identity: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: 'fit-content',
        maxWidth: '100%',
        margin: '0 auto',
        padding: '5px 14px 5px 5px',
        borderRadius: '999px',
        backgroundColor: 'var(--ds-bg-primary)',
        boxShadow: '0 3px 10px rgba(9, 24, 58, 0.10)',
    },
    avatar: {
        position: 'relative',
        width: '26px',
        height: '26px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    online: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: '#22C55E',
        border: '1.5px solid #ffffff',
        boxSizing: 'border-box',
    },
    name: {
        fontSize: '11px',
        fontWeight: 700,
        color: '#09183A',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    body: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '7px',
        padding: '6px 12px 10px',
        overflow: 'hidden',
    },
    quickReplies: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginTop: '2px',
    },
    footer: {
        flexShrink: 0,
        padding: '8px 10px 10px',
    },
    inputRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '4px 4px 4px 10px',
        borderRadius: '20px',
        border: '1px solid rgba(9, 24, 58, 0.10)',
    },
    placeholder: {
        flex: 1,
        color: '#A4ADC1',
        fontSize: '11px',
    },
    send: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        flexShrink: 0,
    },
};

const bubble = (background, color, radius) => ({
    maxWidth: '78%',
    padding: '8px 11px',
    borderRadius: radius,
    backgroundColor: background,
    color: color,
    wordBreak: 'break-word',
});

const quickReply = (borderColor, color) => ({
    padding: '5px 11px',
    borderRadius: '999px',
    border: '1px solid ' + borderColor,
    color: color,
    fontSize: '10px',
});

const QUICK_REPLIES = ['Orders & Returns', 'Delivery', 'Complaint'];

const glyph = (children) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        {children}
    </svg>
);

const MENU_ITEMS = [
    {label: 'New chat', icon: glyph(<React.Fragment><path d="M12 5v14"/><path d="M5 12h14"/></React.Fragment>)},
    {label: 'Chat history', icon: glyph(<React.Fragment><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></React.Fragment>)},
    {label: 'Screenshot', icon: glyph(<React.Fragment><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M9 21h6"/></React.Fragment>)},
];

const WebChatDemo = ({
                         headerText,
                         logoUrl,
                         headerBackground,
                         bodyBackground,
                         botMessageBackground,
                         userMessageBackground,
                         footerBackground,
                         userTextColor,
                         botTextColor,
                         quickReplyTextColor,
                     }) => (
    <div style={styles.frame}>
        <div style={{...styles.header, backgroundColor: bodyBackground}}>
            <div style={styles.headerBar}>
                <div style={styles.action}/>
                <div style={styles.actionGroup}>
                    <div style={styles.action}/>
                    <div style={styles.action}/>
                </div>
            </div>

            <div style={{...styles.menu, backgroundColor: botMessageBackground, color: botTextColor}}>
                {MENU_ITEMS.map(item => (
                    <div key={item.label} style={styles.menuRow}>
                        <span style={{...styles.menuIcon, color: userMessageBackground}}>{item.icon}</span>
                        <span>{item.label}</span>
                    </div>
                ))}
            </div>

            <div style={styles.identity}>
                <div style={{...styles.avatar, backgroundColor: headerBackground}}>
                    {logoUrl && <img src={logoUrl} alt="" style={{width: '100%', height: '100%', objectFit: 'contain'}}/>}
                    <span style={styles.online}/>
                </div>
                <div style={{...styles.name, minWidth: 0}}>{headerText || 'Virtual Assistant'}</div>
            </div>
        </div>

        <div style={{...styles.body, backgroundColor: bodyBackground}}>
            <div style={bubble(botMessageBackground, botTextColor, '14px 14px 14px 4px')}>
                Hello! How can I help you today?
            </div>

            <div style={{...bubble(userMessageBackground, userTextColor, '14px 14px 4px 14px'), alignSelf: 'flex-end'}}>
                I have a question about my order
            </div>

            <div style={bubble(botMessageBackground, botTextColor, '14px 14px 14px 4px')}>
                Sure - what would you like to know?
            </div>

            <div style={styles.quickReplies}>
                {QUICK_REPLIES.map(label => (
                    <span key={label} style={quickReply(userMessageBackground, quickReplyTextColor)}>
                        {label}
                    </span>
                ))}
            </div>
        </div>

        <div style={{...styles.footer, backgroundColor: bodyBackground}}>
            <div style={{...styles.inputRow, backgroundColor: footerBackground}}>
                <span style={styles.placeholder}>Type your message</span>
                {/* the arrow wears the user message text colour, as it does in the widget */}
                <div style={{...styles.send, backgroundColor: userMessageBackground, color: userTextColor}}>
                    <svg width="11" height="11" viewBox="0 0 26 20" fill="currentColor">
                        <path d="M24.93 9.3 1.29.07a.9.9 0 0 0-1.09.21.86.86 0 0 0-.02.95L7.95 10 .18 18.77a.87.87 0 0 0 .02.95c.18.18.44.28.71.28.13 0 .26-.02.38-.07l23.64-9.23c.32-.13.53-.4.53-.7 0-.3-.21-.57-.53-.7Z"/>
                    </svg>
                </div>
            </div>
        </div>
    </div>
);

WebChatDemo.propTypes = {
    headerText: PropTypes.string,
    headerBackground: PropTypes.string.isRequired,
    bodyBackground: PropTypes.string.isRequired,
    botMessageBackground: PropTypes.string.isRequired,
    userMessageBackground: PropTypes.string.isRequired,
    footerBackground: PropTypes.string.isRequired,
    botTextColor: PropTypes.string.isRequired,
    userTextColor: PropTypes.string.isRequired,
    quickReplyTextColor: PropTypes.string.isRequired,
};

export default WebChatDemo;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/webChatDesignAndConfig/components/WebChatDemo.jsx